import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import jsPDF from 'jspdf';
import { Observable, Subject } from 'rxjs';
import { workbenchDataResponseModel } from 'src/app/model/ecb-workbench.model';
import { apiConstant } from 'src/app/utility/constant/api-constant';

import { TreeNode, tableDataItem } from '../components/table-view/table-view';
import { ProjectMetaData } from '../../screening-of-activities/soa-constants/types';

@Injectable({
    providedIn: 'root',
})
export class DesignerSuiteService {
    private readonly apiEndpoint = import.meta.env.NG_APP_API_URL;
    private data!: tableDataItem;
    private readonly http = inject(HttpClient);
    apiTimeStamps: Record<string, string> = {
        fetchDraft: '',
        fetchProjectEC: '',
        fetchPromptInstruction: '',
        fetchWorkbenchStudyInput: '',
        generateHeatmap: '',
        inclusivitySaveCriteria: '',
        saveHeatmap: '',
    };
    public createProject!: string;

    public updateElementTitle = new Subject<string>();

    /** Emits which SOA name editor is active ('header' | 'tab'), so the other can auto-save and close. */
    public activeSOANameEditor = new Subject<'header' | 'tab'>();

    ecComments: Record<string, any[]> | undefined;
    projectFilteredData: any = null;

    private readonly _eventProjectMetaData = new Subject<ProjectMetaData>();
    eventsProjectMetaData$ = this._eventProjectMetaData.asObservable();
    emitProjectMetaData(payload: ProjectMetaData) {
        this._eventProjectMetaData.next(payload);
    }

    convertToTree(rawData: string[], delimiter: string = '/'): TreeNode[] {
        const rootNodes: TreeNode[] = [];
        const nodeMap: { [key: string]: TreeNode } = {};
        rawData.forEach((entry) => {
            const parts = entry.split(delimiter);
            let currentPath = '';
            let parentNode: any = null;

            parts.forEach((part, index) => {
                currentPath = currentPath
                    ? `${currentPath}${delimiter}${part}`
                    : part;
                if (!nodeMap[currentPath]) {
                    const newNode: TreeNode = {
                        children: [],
                        id: currentPath,
                        name: part,
                    };
                    nodeMap[currentPath] = newNode;
                    if (parentNode) {
                        parentNode.children.push(newNode);
                    } else {
                        rootNodes.push(newNode);
                    }
                }
                parentNode = nodeMap[currentPath];
            });
        });
        return rootNodes;
    }

    deleteProject(id: string, updated: string): Observable<any> {
        return this.http.delete(
            this.apiEndpoint +
                `${apiConstant.deleteProject}?project_id=${id}&updated_on=${updated}`
        );
    }

    public downloadAsPDF(
        elementId: string,
        projectName: string,
        selectedTab = ''
    ) {
        const contentToConvert = document.getElementById(
            elementId
        ) as HTMLElement;
        const headContent = document.querySelector(
            '#audit-log-header-id'
        ) as HTMLElement;
        const listContent = document.querySelector(
            '.audit-list-container'
        ) as HTMLElement;
        contentToConvert.style.fontFamily = 'Arial';
        contentToConvert.style.letterSpacing = '1px';
        const initWidth = listContent.style.width;
        listContent.style.width = '750px';
        const subName = projectName.substring(0, 20).trim();
        const name = subName.toLowerCase().replaceAll(' ', '_');
        const heightDiff =
            listContent.scrollHeight > listContent.offsetHeight
                ? 0
                : listContent.scrollHeight * 0.5;
        const widthDiff =
            listContent.scrollHeight > listContent.offsetHeight
                ? 0
                : listContent.scrollHeight * 0.02;
        const doc = new jsPDF({
            format: [
                770 + widthDiff,
                listContent.scrollHeight +
                    headContent.offsetHeight +
                    heightDiff +
                    20,
            ],
            orientation: 'portrait',
            unit: 'px',
        });
        doc.setFontSize(16);

        doc.html(contentToConvert, {
            autoPaging: 'text',
            callback: (doc) => {
                doc.save(
                    `${name}_log_${selectedTab.toLowerCase().replaceAll(' ', '_')}.pdf`
                );
                listContent.style.width = initWidth;
                contentToConvert.style.fontFamily = 'roche-sans';
                contentToConvert.style.letterSpacing = 'normal';
            },
        });
    }

    fetchProject(project_id: string): Observable<any> {
        return this.http.get(
            this.apiEndpoint +
                apiConstant.fetchProjectEC +
                `?project_id=${project_id}`
        );
    }

    checkAuthAndFetchProject(project_id: string): Observable<any> {
        return this.http.get(
            this.apiEndpoint +
                apiConstant.checkAuthentication +
                `?instance_id=${project_id}`
        );
    }

    fetchProjectAuditLog(project_id: number) {
        return this.http.get(
            this.apiEndpoint +
                `${apiConstant.fetchProjectAuditLog}?project_id=${project_id}`
        );
    }

    fetchUserList(term: string): Observable<any> {
        return this.http.get<any>(
            this.apiEndpoint +
                `${apiConstant.fetchUsersList}?search_string=${encodeURIComponent(term)}`
        );
    }

    getApiTimestamp(apiKey: string): string {
        return this.apiTimeStamps[apiKey];
    }

    getData(): tableDataItem {
        return this.data;
    }

    getIndicationName(indication: string): string {
        if (!indication.includes('/')) {
            return indication;
        }
        let sections = indication.split(/\/+/);
        sections = sections.map((value: string) => value.trim());
        return sections.at(-1)?.trim() ?? '';
    }

    getProjectDetails(): Observable<any> {
        return this.http.get(this.apiEndpoint + apiConstant.fetchProjectList);
    }

    getProjectFilterValues(): Observable<any> {
        return this.http.get(
            this.apiEndpoint + apiConstant.fetchProjectFilterValues
        );
    }

    getWorkbenchData(
        instanceId: string,
        workbenchId = 0
    ): Observable<workbenchDataResponseModel> {
        return this.http.get<workbenchDataResponseModel>(
            this.apiEndpoint +
                apiConstant.fetchWorkbenchStudyInput +
                `?instance_id=${instanceId}&workbench_id=${workbenchId}`
        );
    }

    saveProject(projectObj: any): Observable<any> {
        return this.http.post(
            this.apiEndpoint + apiConstant.saveProject,
            projectObj
        );
    }

    setApiTimestamp(apiKey: string, value: string): void {
        this.apiTimeStamps[apiKey] = value;
    }

    setData(data: tableDataItem) {
        this.data = data;
    }

    updateInstanceTitle(instance_id: string, title: string) {
        return this.http.post(
            this.apiEndpoint + apiConstant.updateInstanceTitle,
            {
                instance_id,
                title,
            }
        );
    }

    updateProject(projectDetails: tableDataItem): Observable<any> {
        return this.http.post(this.apiEndpoint + apiConstant.saveProject, {
            project_id: projectDetails.project_id,
            project_name: projectDetails.project_name,
        });
    }
}
